import {
  coachShortDisplayName,
  groupCoachProfilesByUser,
} from '@efdigital/ll-addon-coaching';
import type { CoachProfile, UserGroup } from '@efdigital/ll-addon-data';
import { CoachPicker } from '@efdigital/ll-admin-v2/components/pickers/coach-picker.gts';
import {
  dummyUserCoach,
  isDummyUser,
} from '@efdigital/ll-admin-v2/utils/user-groups';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

interface EditCoachPoolMembersSignature {
  Args: {
    pool: UserGroup;
    coaches: CoachProfile[];
    readonly?: boolean;
  };
}

/**
 * Edits the membership of a single coach pool (a UserGroup). Members are stored
 * as coach user ids; coach profiles are used only to resolve details for
 * display and to populate the picker. A coach must have a linked platform
 * account (`user`) to be added.
 */
export class EditCoachPoolMembers extends Component<EditCoachPoolMembersSignature> {
  // One profile per user (the published one) — profiles can also exist in
  // pending_review status for the same user and must not double-count.
  get publishedByUserId(): Map<string, CoachProfile> {
    const map = new Map<string, CoachProfile>();

    for (const coach of groupCoachProfilesByUser(this.args.coaches).published) {
      if (coach.user) {
        map.set(coach.user, coach);
      }
    }

    return map;
  }

  memberName = (userId: string): string => {
    const coach = this.publishedByUserId.get(userId);

    return coach ? coachShortDisplayName(coach) || userId : userId;
  };

  memberHint = (userId: string): string | null => {
    const coach = this.publishedByUserId.get(userId);

    if (!coach) {
      return null;
    }

    return coach.acceptingCoachees ? null : 'Not accepting coachees';
  };

  get realMemberIds(): string[] {
    return (this.args.pool.userIds ?? []).filter((id) => !isDummyUser(id));
  }

  addCoach = (coach: CoachProfile) => {
    if (!coach.user || this.realMemberIds.includes(coach.user)) {
      return;
    }

    // Strip any dummy placeholders now that a real user is being added.
    const current = (this.args.pool.userIds ?? []).filter(
      (id) => !isDummyUser(id)
    );
    this.args.pool.userIds = [...current, coach.user];
  };

  removeMember = (userId: string) => {
    const remaining = (this.args.pool.userIds ?? []).filter(
      (id) => id !== userId
    );
    // Keep at least the dummy placeholder so the API accepts an empty pool.
    const realRemaining = remaining.filter((id) => !isDummyUser(id));
    this.args.pool.userIds =
      realRemaining.length > 0 ? remaining : [dummyUserCoach];
  };

  <template>
    <label class="form-label small">Members ({{this.realMemberIds.length}})</label>
    {{#if this.realMemberIds.length}}
      <div class="d-flex flex-wrap column-gap-2 row-gap-1 mb-2">
        {{#each this.realMemberIds as |userId|}}
          <span
            class="badge rounded-pill bg-body-secondary text-body d-inline-flex align-items-center gap-1"
          >{{this.memberName userId}}
            {{#if (this.memberHint userId)}}
              <iconify-icon
                inline
                icon="ph:pause-circle"
                class="text-warning-emphasis"
                title={{this.memberHint userId}}
                aria-label={{this.memberHint userId}}
              />
            {{/if}}
            {{#unless @readonly}}
              <button
                type="button"
                class="btn-close btn-close-sm"
                aria-label="Remove {{this.memberName userId}}"
                {{on "click" (fn this.removeMember userId)}}
              ></button>
            {{/unless}}
          </span>
        {{/each}}
      </div>
    {{else}}
      <p class="text-muted small mb-2">No coaches in this pool yet.</p>
    {{/if}}

    {{#unless @readonly}}
      <CoachPicker
        @coaches={{@coaches}}
        @memberUserIds={{this.realMemberIds}}
        @onAdd={{this.addCoach}}
        @onRemove={{this.removeMember}}
      >
        <:input as |showModal|>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            data-test-browse-coaches
            {{on "click" showModal}}
          >
            <iconify-icon inline icon="ph:users" aria-hidden="true" />
            Browse coaches…
          </button>
        </:input>
      </CoachPicker>
    {{/unless}}
  </template>
}
